/**
 * Feb 2026 · Project Tickets tab (replaces Issues).
 * Reuses the workspace helpdesk endpoint filtered to this project.
 * Editable inline: change status/priority/assignee. "Log ticket" button
 * opens the ticket create flow pre-filled with this project.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Ticket, Plus, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";

const STATUSES = ["open", "in_progress", "waiting", "resolved", "closed"];
const PRIORITIES = ["low", "medium", "high", "urgent"];

export default function ProjectTicketsTab({ projectId, project }) {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [members, setMembers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [busy, setBusy] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const [t, m, q] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/helpdesk/tickets`, { params: { project_id: projectId, limit: 500 } }),
        api.get(`/workspaces/${activeWorkspaceId}/members`),
        api.get(`/workspaces/${activeWorkspaceId}/helpdesk/queues`).catch(() => ({ data: [] })),
      ]);
      setRows(t.data?.rows || t.data || []);
      setMembers(m.data || []);
      setQueues(q.data?.rows || q.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load tickets")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, projectId]);

  const filtered = useMemo(() => {
    if (!filterStatus) return rows;
    return rows.filter((r) => r.status === filterStatus);
  }, [rows, filterStatus]);

  const patch = async (tid, field, value) => {
    try {
      if (field === "status") {
        await api.post(`/helpdesk/tickets/${tid}/transition`, { status: value });
      } else if (field === "assignee_id") {
        await api.post(`/helpdesk/tickets/${tid}/assign`, { assignee_id: value || null });
      } else {
        await api.patch(`/helpdesk/tickets/${tid}`, { [field]: value || null });
      }
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  const counts = useMemo(() => {
    const out = { all: rows.length };
    STATUSES.forEach((s) => { out[s] = rows.filter((r) => r.status === s).length; });
    return out;
  }, [rows]);

  return (
    <div className="space-y-3" data-testid="project-tickets-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <Ticket className="w-4 h-4 text-primary" />
        <span className="text-xs font-semibold">Tickets · {counts.all}</span>
        <div className="flex items-center gap-1 flex-wrap">
          <button className={`text-[10px] px-2 py-0.5 border ${!filterStatus ? "border-primary bg-primary/10" : "border-border hover:bg-muted"}`} onClick={() => setFilterStatus("")} data-testid="proj-tkt-filter-all">
            All {counts.all}
          </button>
          {STATUSES.map((s) => (
            <button key={s} className={`text-[10px] px-2 py-0.5 border capitalize ${filterStatus === s ? "border-primary bg-primary/10" : "border-border hover:bg-muted"}`} onClick={() => setFilterStatus(filterStatus === s ? "" : s)} data-testid={`proj-tkt-filter-${s}`}>
              {s.replace("_", " ")} {counts[s] || 0}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" className="rounded-none h-8 text-xs" onClick={() => setShowNew(true)} data-testid="proj-tkt-log-btn">
            <Plus className="w-3 h-3 mr-1" /> Log ticket
          </Button>
          <Link to="/app/helpdesk" className="text-[10px] text-primary underline">Open full Helpdesk →</Link>
        </div>
      </div>
      {busy ? (
        <div className="p-6 text-xs text-muted-foreground text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground">
          No tickets linked to this project. Click <b>Log ticket</b> above to create one.
        </div>
      ) : (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest text-[10px]">Ticket</th>
                <th className="text-left px-2 py-2 font-semibold uppercase tracking-widest text-[10px]">Status</th>
                <th className="text-left px-2 py-2 font-semibold uppercase tracking-widest text-[10px]">Priority</th>
                <th className="text-left px-2 py-2 font-semibold uppercase tracking-widest text-[10px]">Assignee</th>
                <th className="text-left px-2 py-2 font-semibold uppercase tracking-widest text-[10px]">Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, i) => (
                <tr key={t.id} className="border-t border-border hover:bg-muted/30" data-testid={`proj-tkt-row-${i}`}>
                  <td className="px-3 py-2 align-top">
                    <Link to={`/app/helpdesk/tickets/${t.id}`} className="font-semibold hover:underline">{t.subject || t.title || "(untitled)"}</Link>
                    {t.ticket_number && <div className="text-[10px] text-muted-foreground">#{t.ticket_number}</div>}
                  </td>
                  <td className="px-2 py-2 align-top">
                    <select value={t.status || ""} onChange={(e) => patch(t.id, "status", e.target.value)} className="h-7 border border-border bg-background text-[11px] px-1 rounded-none capitalize" data-testid={`proj-tkt-status-${i}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-2 align-top">
                    <select value={t.priority || "medium"} onChange={(e) => patch(t.id, "priority", e.target.value)} className="h-7 border border-border bg-background text-[11px] px-1 rounded-none capitalize" data-testid={`proj-tkt-priority-${i}`}>
                      {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-2 align-top">
                    <select value={t.assignee_id || ""} onChange={(e) => patch(t.id, "assignee_id", e.target.value)} className="h-7 border border-border bg-background text-[11px] px-1 rounded-none max-w-[160px]" data-testid={`proj-tkt-assignee-${i}`}>
                      <option value="">— Unassigned —</option>
                      {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-2 align-top text-[10px] text-muted-foreground whitespace-nowrap">{fmtRelative(t.updated_at || t.created_at)}</td>
                  <td className="px-2 py-2 align-top">
                    <Link to={`/app/helpdesk/tickets/${t.id}`} className="text-primary inline-flex items-center gap-1 text-[11px]">Open <ArrowUpRight className="w-3 h-3" /></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showNew && (
        <NewTicketDialog wsId={activeWorkspaceId} project={project} queues={queues}
                          onClose={() => setShowNew(false)}
                          onCreated={() => { setShowNew(false); load(); }} />
      )}
    </div>
  );
}

function NewTicketDialog({ wsId, project, queues = [], onClose, onCreated }) {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [queueId, setQueueId] = useState(queues[0]?.id || "");
  const [busy, setBusy] = useState(false);
  // Auto-select first queue when queues load asynchronously
  useEffect(() => { if (!queueId && queues.length) setQueueId(queues[0].id); }, [queues]);   // eslint-disable-line

  const submit = async () => {
    if (!subject.trim()) { toast.error("Subject required"); return; }
    if (!queueId) { toast.error("No helpdesk queue configured. Set one up in Helpdesk → Settings."); return; }
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/helpdesk/tickets`, {
        queue_id: queueId,
        subject: subject.trim(),
        description_html: description.trim() || null,
        priority,
        project_id: project?.id,
        client_id: project?.client_id || null,
        source: "internal",
      });
      toast.success("Ticket logged");
      onCreated?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Log failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-none" data-testid="proj-tkt-new-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <Ticket className="w-4 h-4 text-primary" /> Log ticket for {project?.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold">Subject *</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} autoFocus className="h-10 rounded-none" data-testid="proj-tkt-new-subject" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Description</Label>
            <Textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="proj-tkt-new-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Queue *</Label>
              <select value={queueId} onChange={(e) => setQueueId(e.target.value)} className="w-full h-10 border border-border px-2 text-sm bg-background rounded-none" data-testid="proj-tkt-new-queue">
                {queues.length === 0 && <option value="">— No queues configured —</option>}
                {queues.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Priority</Label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className="w-full h-10 border border-border px-2 text-sm bg-background rounded-none capitalize" data-testid="proj-tkt-new-priority">
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy || !subject.trim() || !queueId} data-testid="proj-tkt-new-save">
            {busy ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Logging…</> : "Log ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
